import { FC, Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { t } from "i18next";

import TableListGyms from "@/shared/components/organisms/TableListGyms";
import { toasts } from "@/shared/utils/toasts";

import { useProductsQuery } from "@/app/subscription/subscription.api";
import { selectUser } from "@/app/auth/auth.slice";

import { useCountriesQuery } from "@/app/app.apiCountry";
import { useStatesQuery, useTimezonesQuery } from "@/app/app.apiStates";

//Imports API
import {
  useCreateBranchMutation,
  useFetchBranchsMutation,
} from "@/app/branchs/branchs.api";
import { QueryMetaData } from "@/app/gyms/gyms";
import { useDebounce } from "@/shared/hooks/debounce";
import { BranchForm } from "@/shared/components/organisms/BranchForm";
import { useGymsQuery } from "@/app/gyms/gyms.api";

export const Gyms: FC<any> = () => {
  const [open, setOpen] = useState(false);
  const [openKpi, setOpenKpi] = useState(false);
  const [page, setPage] = useState(1);
  const [entries, setEntries] = useState(50);
  const [queryMetaData, setQueryMetaData] = useState<QueryMetaData>({
    page: 1,
    perPage: 1,
    total: 1,
    lastPage: 1,
  });
  const [search, setSearch] = useState<string>("");
  const [branchsTable, setBranchsTable] = useState([]);

  // ALL React hooks (useSearchParams, useNavigate, etc.) must always be called
  const [searchParams] = useSearchParams();
  const user = useSelector(selectUser);

  const gymId = searchParams.get("gymId");
  const handleOpenKpi = () => setOpenKpi(!openKpi);

  const [createBranch, createBranchResult] = useCreateBranchMutation();
  const [fetchBranchs, { isLoading }] = useFetchBranchsMutation();
  const timezonesQuery = useTimezonesQuery(undefined);
  const countriesQuery = useCountriesQuery(undefined);
  const statesQuery = useStatesQuery(undefined);
  const gymsQuery = useGymsQuery({
    branchId: user?.branch?.id,
  });

  const debouncedSearch = useDebounce(search, 500);

  const productsQuery = useProductsQuery(
    {
      branchId: user?.branch?.id,
    },
    {
      skip: !user?.branch?.id,
    }
  );

  useEffect(() => {
    fetchData();
  }, [debouncedSearch, page, entries, gymId]);

  // Helper functions
  const handleSubmitCreateBranch = async (values: any) => {
    try {
      const formToSend = new FormData();
      formToSend.append("gym_id", values.gym_id);
      formToSend.append("branch", values.branch);
      formToSend.append("profile_avatar", values.profile_avatar);
      formToSend.append("large_logo", values.large_logo);
      formToSend.append("first_color", values.first_color);
      formToSend.append("secound_color", values.secound_color);
      formToSend.append("video_tutorial", values.video_tutorial);
      formToSend.append("phone", values.phone);
      formToSend.append("email", values.email);
      formToSend.append("country", values.country);
      formToSend.append("country_code", values.countryCode);
      formToSend.append("state", values.state);
      formToSend.append("state_code", values.stateCode);
      formToSend.append("population", values.population);
      formToSend.append("adress", values.address);
      formToSend.append("code_zip", values.code_zip);
      formToSend.append("timezone", values.timezone);
      formToSend.append("app_ios", values.app_ios);
      formToSend.append("app_android", values.app_android);

      if (values.is_chat) {
        formToSend.append("is_chat", "on");
      } else {
        formToSend.append("is_chat", "off");
      }
      formToSend.append("sets", String(values.sets));

      if (values.block_training) {
        formToSend.append("block_training", "on");
      } else {
        formToSend.append("block_training", "off");
      }

      if (values.block_nutrition) {
        formToSend.append("block_nutrition", "on");
      } else {
        formToSend.append("block_nutrition", "off");
      }

      if (values.compliance_schedule) {
        formToSend.append("compliance_schedule", "on");
      } else {
        formToSend.append("compliance_schedule", "off");
      }

      if (values.customer_to_gym_subscription) {
        formToSend.append("customer_to_gym_subscription", "on");
      } else {
        formToSend.append("customer_to_gym_subscription", "off");
      }

      if (values.multi_center_quotas) {
        formToSend.append("multi_center_quotas", "on");
      } else {
        formToSend.append("multi_center_quotas", "off");
      }

      if (values.uses_lesson) {
        formToSend.append("uses_lesson", "on");
      } else {
        formToSend.append("uses_lesson", "off");
      }

      if (values.cameras_module) {
        formToSend.append("cameras_module", "on");
      } else {
        formToSend.append("cameras_module", "off");
      }
      formToSend.append("rule_penalty", String(values.rule_penalty));
      formToSend.append("rule_max", String(values.rule_max));

      if (values.uses_assisteds) {
        formToSend.append("uses_assisteds", "on");
      } else {
        formToSend.append("uses_assisteds", "off");
      }

      if (values.slots) {
        formToSend.append("slots", "on");
      } else {
        formToSend.append("slots", "off");
      }

      if (values.catchment_module) {
        formToSend.append("catchment_module", "on");
      } else {
        formToSend.append("catchment_module", "off");
      }

      if (values.recovery_module) {
        formToSend.append("recovery_module", "on");
      } else {
        formToSend.append("recovery_module", "off");
      }

      if (values.ticket_module) {
        formToSend.append("ticket_module", "on");
      } else {
        formToSend.append("ticket_module", "off");
      }

      if (values.store) {
        formToSend.append("store", "on");
      } else {
        formToSend.append("store", "off");
      }

      if (values.course_module) {
        formToSend.append("course_module", "on");
      } else {
        formToSend.append("course_module", "off");
      }

      if (values.access_module) {
        formToSend.append("access_module", "on");
      } else {
        formToSend.append("access_module", "off");
      }

      formToSend.append(
        "access_module_type",
        String(values.access_module_type)
      );
      if (values.type_branch === 1) {
        formToSend.append("type_branch", "gimnasio");
      } else {
        formToSend.append("type_branch", "entrenador_personal");
      }

      formToSend.append("contract", values.contract);

      if (values.available_marketing) {
        for (const marketing of values.available_marketing) {
          formToSend.append("available_marketing[]", marketing.id);
        }
      }

      if (values.accounting_module) {
        formToSend.append("accounting_module", "on");
      } else {
        formToSend.append("accounting_module", "off");
      }

      if (values.direct_debit_module) {
        formToSend.append("direct_debit_module", "on");
      } else {
        formToSend.append("direct_debit_module", "off");
      }

      formToSend.append("tax_authorities", String(values.tax_authorities));
      formToSend.append("gti_code", values.gti_code);
      formToSend.append("gti_user", values.gti_user);
      formToSend.append("gti_password", values.gti_password);
      formToSend.append("gti_terminal", values.gti_terminal);
      if (values.payment_card) {
        formToSend.append("payment_card", "on");
      } else {
        formToSend.append("payment_card", "off");
      }
      formToSend.append("gateway", values.gateway);
      formToSend.append("public_key_payment", values.public_key_payment);
      formToSend.append("private_key_payment", values.private_key_payment);
      formToSend.append("prod_integrity", values.prod_integrity);

      if (values.uses_subscription) {
        formToSend.append("uses_subscription", "on");
      } else {
        formToSend.append("uses_subscription", "off");
      }

      if (values.available_subscriptions) {
        for (const subscription of values.available_subscriptions) {
          formToSend.append("available_subscriptions[]", subscription.id);
        }
      }

      formToSend.append("contract_id", String(values.contract_id));

      if (values.subscription_blocking) {
        formToSend.append("subscription_blocking", "on");
      } else {
        formToSend.append("subscription_blocking", "off");
      }

      formToSend.append("trial_period_days", String(values.trial_period_days));
      for (const [key, value] of formToSend.entries()) {
        console.log(`${key}:`, value);
      }

      await createBranch(formToSend).unwrap();
      setOpen(false);
      fetchData();
      toasts.success(
        "Filial registrada",
        "La filial se ha registrado correctamente"
      );
    } catch (error) {
      const message = error?.data?.message ?? undefined;
      console.log("Error on handleSubmit", error);
      console.log({ message });

      if (
        message &&
        (message as string).includes(
          "Integrity constraint violation: 1062 Duplicate entry"
        )
      ) {
        toasts.danger(
          "Algo salió mal",
          "Ya existe una filial con este mismo nombre"
        );

        return;
      }
      toasts.danger(
        "Error al registrar la filial",
        "Ha ocurrido un error al registrar la filial"
      );
    }
  };

  // Función para manejar cambios de página
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleEntriesChange = (entries: number) => {
    setEntries(entries);
  };

  async function fetchData() {
    try {
      const response = await fetchBranchs({
        search: debouncedSearch,
        page: debouncedSearch ? 1 : page,
        limit: entries,
        gymId: gymId,
      }).unwrap();
      const arrayBranchs = [];
      let objectBranchs = {};
      setQueryMetaData({
        page: response.current_page,
        perPage: response.per_page,
        total: response.total,
        lastPage: response.last_page,
      });
      setEntries(response.per_page);
      for (let i = 0; i < response.data.length; i++) {
        objectBranchs = {
          id: response.data[i].id,
          logo: response.data[i].logo,
          branch: response.data[i].branch,
          email: response.data[i].email,
          web: response.data[i].web,
          phone: response.data[i].phone,
          country: response.data[i].country,
          code_zip: response.data[i].code_zip,
          state: response.data[i].state,
          population: response.data[i].population,
          active_users_count: response.data[i].active_users_count,
        };
        arrayBranchs.push(objectBranchs);
      }
      setBranchsTable(arrayBranchs);
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <Fragment>
      <div className="flex flex-col items-center h-full w-full mt-8">
        <div className="w-full mx-auto mb-8">
          <TableListGyms
            filiales={branchsTable}
            title={t("branch.views.branchs")}
            description={t("branch.views.list_branchs")}
            onClickTop={() => setOpen(true)}
            handlePageChange={handlePageChange}
            handleEntriesChange={handleEntriesChange}
            entries={entries}
            queryMetaData={queryMetaData}
            isLoading={isLoading}
            searchQuery={search}
            onSearchChange={(e) => setSearch(e.target.value)}
            stats={productsQuery.data ?? []}
            openKpi={openKpi}
            OnClickKPI={handleOpenKpi}
          />
        </div>
      </div>

      <BranchForm
        visible={open}
        setVisible={setOpen}
        onSubmit={async (values: any) => handleSubmitCreateBranch(values)}
        gyms={gymsQuery.data ?? []}
        branch={undefined}
        countries={countriesQuery.data ?? []}
        states={statesQuery.data ?? []}
        timezones={timezonesQuery.data}
        createMode
        isLoadingCreateUpdate={createBranchResult.isLoading}
      />

      {/* <Formik
        enableReinitialize
        initialValues={{
          ...initialFormData,
          country: SelectedCountry?.id || "",
          sets: selectedSets?.id || "",
          rule_penalty: optionPenalizacion[0].id || null,
          access_module_type: optionControlAccess[0].id || null,
          type_branch: optionCreacionUsers[0].id || null,
          gateway: optionPagos[0].name || "",
          contract_id: 1,
        }}
        validationSchema={branchValidationSchema}
        validateOnMount={false}
        onSubmit={async (values, formikHelpers) => {
          try {
            const formToSend = new FormData();
            formToSend.append("gym_id", values.gym_id);
            formToSend.append("branch", values.branch);
            formToSend.append("profile_avatar", values.profile_avatar);
            formToSend.append("large_logo", values.large_logo);
            formToSend.append("first_color", values.first_color);
            formToSend.append("secound_color", values.secound_color);
            formToSend.append("video_tutorial", values.video_tutorial);
            formToSend.append("phone", values.phone);
            formToSend.append("email", values.email);
            formToSend.append("country", values.country);
            formToSend.append("state", values.state);
            formToSend.append("population", values.population);
            formToSend.append("adress", values.adress);
            formToSend.append("code_zip", values.code_zip);
            formToSend.append("timezone", values.timezone);
            formToSend.append("app_ios", values.app_ios);
            formToSend.append("app_android", values.app_android);

            if (values.is_chat) {
              formToSend.append("is_chat", "on");
            } else {
              formToSend.append("is_chat", "off");
            }
            formToSend.append("sets", String(values.sets));

            if (values.block_training) {
              formToSend.append("block_training", "on");
            } else {
              formToSend.append("block_training", "off");
            }

            if (values.block_nutrition) {
              formToSend.append("block_nutrition", "on");
            } else {
              formToSend.append("block_nutrition", "off");
            }

            if (values.compliance_schedule) {
              formToSend.append("compliance_schedule", "on");
            } else {
              formToSend.append("compliance_schedule", "off");
            }

            if (values.customer_to_gym_subscription) {
              formToSend.append("customer_to_gym_subscription", "on");
            } else {
              formToSend.append("customer_to_gym_subscription", "off");
            }

            if (values.multi_center_quotas) {
              formToSend.append("multi_center_quotas", "on");
            } else {
              formToSend.append("multi_center_quotas", "off");
            }

            if (values.uses_lesson) {
              formToSend.append("uses_lesson", "on");
            } else {
              formToSend.append("uses_lesson", "off");
            }

            if (values.cameras_module) {
              formToSend.append("cameras_module", "on");
            } else {
              formToSend.append("cameras_module", "off");
            }
            formToSend.append("rule_penalty", String(values.rule_penalty));
            formToSend.append("rule_max", String(values.rule_max));

            if (values.uses_assisteds) {
              formToSend.append("uses_assisteds", "on");
            } else {
              formToSend.append("uses_assisteds", "off");
            }

            if (values.slots) {
              formToSend.append("slots", "on");
            } else {
              formToSend.append("slots", "off");
            }

            if (values.catchment_module) {
              formToSend.append("catchment_module", "on");
            } else {
              formToSend.append("catchment_module", "off");
            }

            if (values.recovery_module) {
              formToSend.append("recovery_module", "on");
            } else {
              formToSend.append("recovery_module", "off");
            }

            if (values.ticket_module) {
              formToSend.append("ticket_module", "on");
            } else {
              formToSend.append("ticket_module", "off");
            }

            if (values.store) {
              formToSend.append("store", "on");
            } else {
              formToSend.append("store", "off");
            }

            if (values.course_module) {
              formToSend.append("course_module", "on");
            } else {
              formToSend.append("course_module", "off");
            }

            if (values.access_module) {
              formToSend.append("access_module", "on");
            } else {
              formToSend.append("access_module", "off");
            }

            formToSend.append(
              "access_module_type",
              String(values.access_module_type)
            );
            if (values.type_branch === 1) {
              formToSend.append("type_branch", "gimnasio");
            } else {
              formToSend.append("type_branch", "entrenador_personal");
            }

            formToSend.append("contract", values.contract);

            if (values.available_marketing) {
              for (const marketing of values.available_marketing) {
                formToSend.append("available_marketing[]", marketing);
              }
            }

            if (values.accounting_module) {
              formToSend.append("accounting_module", "on");
            } else {
              formToSend.append("accounting_module", "off");
            }

            if (values.direct_debit_module) {
              formToSend.append("direct_debit_module", "on");
            } else {
              formToSend.append("direct_debit_module", "off");
            }

            formToSend.append(
              "tax_authorities",
              String(values.tax_authorities)
            );
            formToSend.append("gti_code", values.gti_code);
            formToSend.append("gti_user", values.gti_user);
            formToSend.append("gti_password", values.gti_password);
            formToSend.append("gti_terminal", values.gti_terminal);
            if (values.payment_card) {
              formToSend.append("payment_card", "on");
            } else {
              formToSend.append("payment_card", "off");
            }
            formToSend.append("gateway", values.gateway);
            formToSend.append("public_key_payment", values.public_key_payment);
            formToSend.append(
              "private_key_payment",
              values.private_key_payment
            );
            formToSend.append("prod_integrity", values.prod_integrity);

            if (values.uses_subscription) {
              formToSend.append("uses_subscription", "on");
            } else {
              formToSend.append("uses_subscription", "off");
            }

            if (values.available_subscriptions) {
              for (const subscription of values.available_subscriptions) {
                formToSend.append("available_subscriptions[]", subscription.id);
              }
            }

            formToSend.append("contract_id", String(values.contract_id));

            if (values.subscription_blocking) {
              formToSend.append("subscription_blocking", "on");
            } else {
              formToSend.append("subscription_blocking", "off");
            }

            formToSend.append(
              "trial_period_days",
              String(values.trial_period_days)
            );
            for (const [key, value] of formToSend.entries()) {
              console.log(`${key}:`, value);
            }

            await handleSubmitCreateBranch(formToSend);
            formikHelpers.resetForm();
          } catch (error) {
            console.error("Error on handleSubmitCreateBranch", error);
          }
        }}
      >
        {(formik) => {
          return (
            <DialogCreate
              OnclickCreate={formik.handleSubmit}
              OnclickCancel={() => {
                setOpen(false);
                formik.resetForm();
              }}
              onClose={() => {
                setOpen(false);
                formik.resetForm();
              }}
              tittleButtonCreate={t("general.create")}
              tittleButtonCancel={t("general.cancel")}
              open={open}
              setOpen={setOpen}
              tittle={t("branch.views.branchs")}
              subTittle={t("branch.views.creating_branchs")}
              isLoading={isLoadingR || createBranchResult.isLoading}
              acceptDisabled={createBranchResult.isLoading}
            >
              <div className="mt-6 mx-2">
                <div className="space-y-12">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("branch.views.profile")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <Select
                          name="gym_id"
                          span={t("general.mandatory")}
                          selected={
                            formik.values.gym_id
                              ? gyms.find(
                                  (option) => option.id === formik.values.gym_id
                                )
                              : {}
                          }
                          setSelected={(option) => {
                            formik.setFieldValue("gym_id", option.id);
                            setSelectedGym(option);
                          }}
                          options={gyms}
                          tittle={t("branch.views.company")}
                          error={
                            formik?.errors?.gym_id && formik?.touched?.gym_id
                          }
                          textError={formik?.errors?.gym_id as string}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("branch.views.name")}
                          span={t("general.mandatory")}
                          id="branch"
                          name="branch"
                          type="text"
                          placeholder={t("branch.views.name_branch")}
                          value={formik.values.branch}
                          onChange={formik.handleChange}
                          error={
                            formik?.errors?.branch && formik?.touched?.branch
                          }
                          textError={formik?.errors?.branch}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("branch.views.personalitation")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <InputFile
                          name="profile_avatar"
                          id="profile_avatar"
                          label={t("branch.views.logo")}
                          spanButton={t("branch.views.upload_image")}
                          description={t(
                            "branch.views.description_upload_image"
                          )}
                          selectedImage={formik.values.profile_avatar}
                          onFileChange={(file) => {
                            formik.setFieldValue("profile_avatar", file); // Almacena el File original
                          }}
                          onRemoveImage={
                            () => formik.setFieldValue("profile_avatar", null) // Opción para remover la imagen
                          }
                          tittleDelete={t("general.delete_image")}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputFileLarge
                          name="large_logo"
                          id="large_logo"
                          label={t("branch.views.large_logo")}
                          spanButton={t("branch.views.upload_image")}
                          description={t(
                            "branch.views.description_upload_image"
                          )}
                          selectedImage={formik.values.large_logo}
                          onFileChange={(file) => {
                            formik.setFieldValue("large_logo", file); // Almacena el File original
                          }}
                          onRemoveImage={
                            () => formik.setFieldValue("large_logo", null) // Opción para remover la imagen
                          }
                          tittleDelete={t("general.delete_image")}
                        />
                      </div>
                      <div className="col-span-full grid grid-cols-2 gap-8 place-content-center">
                        <InputWithCornerHint
                          label={t("branch.views.color_primary")}
                          span={t("general.mandatory")}
                          id="first_color"
                          name="first_color"
                          type="color"
                          value={formik.values.first_color}
                          onChange={formik.handleChange}
                        />
                        <InputWithCornerHint
                          label={t("branch.views.color_secondary")}
                          span={t("general.mandatory")}
                          id="secound_color"
                          name="secound_color"
                          type="color"
                          value={formik.values.secound_color}
                          onChange={formik.handleChange}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("branch.views.video_tutorial")}
                          span={t("general.mandatory")}
                          id="video_tutorial"
                          name="video_tutorial"
                          type="url"
                          value={formik.values.video_tutorial}
                          onChange={formik.handleChange}
                          error={
                            formik?.errors?.video_tutorial &&
                            formik?.touched?.video_tutorial
                          }
                          textError={formik?.errors?.video_tutorial as string}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("branch.views.company_dates")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <InputWithLeadingIcon
                          label={t("branch.views.phone")}
                          span={t("general.mandatory")}
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder={t("branch.views.phone")}
                          value={formik.values.phone}
                          onChange={formik.handleChange}
                          error={
                            formik?.errors?.phone &&
                            (formik?.touched?.phone as boolean)
                          }
                          textError={formik?.errors?.phone as string}
                          icon={
                            <FontAwesomeIcon
                              icon={faPhone}
                              className="h-4 w-4 text-gray-400"
                            />
                          }
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithLeadingIcon
                          label={t("branch.views.email")}
                          span={t("general.mandatory")}
                          id="email"
                          name="email"
                          type="email"
                          placeholder={t("branch.views.email")}
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          error={
                            formik?.errors?.email &&
                            (formik?.touched?.email as boolean)
                          }
                          textError={formik?.errors?.email as string}
                          icon={
                            <FontAwesomeIcon
                              icon={faAt}
                              className="h-4 w-4 text-gray-400"
                            />
                          }
                        />
                      </div>
                      <div className="col-span-full">
                        <SelectCountry
                          span={t("general.mandatory")}
                          selected={
                            formik?.values?.country
                              ? optionsCountryApi.find(
                                  (option) =>
                                    option.id === formik.values.country
                                )
                              : {}
                          }
                          setSelected={(option) => {
                            formik.setFieldValue("country", option.id);
                            SetSelectedCountry(option as any);
                          }}
                          options={optionsCountryApi}
                          tittle={t("branch.views.country")}
                          error={
                            formik?.errors?.country && formik?.touched?.country
                          }
                          textError={formik?.errors?.country as string}
                        />
                      </div>
                      <div className="col-span-full">
                        <Select
                          span={t("general.optional")}
                          selected={selectedState}
                          setSelected={(option) => {
                            formik.setFieldValue("state", option.id);
                            setSelectedState(option as any);
                          }}
                          options={optionsStateApi}
                          tittle={t("branch.views.state_prov")}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("branch.views.population")}
                          span={t("general.mandatory")}
                          id="population"
                          name="population"
                          type="text"
                          value={formik.values.population}
                          placeholder={t("branch.views.population")}
                          onChange={formik.handleChange}
                          error={
                            formik?.errors?.population &&
                            (formik?.touched?.population as boolean)
                          }
                          textError={formik?.errors?.population as string}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("branch.views.adress")}
                          span={t("general.mandatory")}
                          id="adress"
                          name="adress"
                          type="text"
                          placeholder={t("branch.views.adress")}
                          value={formik.values.adress}
                          onChange={formik.handleChange}
                          error={
                            formik?.errors?.adress &&
                            (formik?.touched?.adress as boolean)
                          }
                          textError={formik?.errors?.adress as string}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("branch.views.code_zip")}
                          span={t("general.mandatory")}
                          id="code_zip"
                          name="code_zip"
                          type="text"
                          placeholder={t("branch.views.code_zip")}
                          value={formik.values.code_zip}
                          onChange={formik.handleChange}
                          error={
                            formik?.errors?.code_zip &&
                            (formik?.touched?.code_zip as boolean)
                          }
                          textError={formik?.errors?.code_zip as string}
                        />
                      </div>
                      <div className="col-span-full">
                        <Select
                          span={t("general.mandatory")}
                          selected={
                            formik?.values?.timezone
                              ? timezones.find(
                                  (option) =>
                                    option.name === formik.values.timezone
                                )
                              : {}
                          }
                          setSelected={(option) => {
                            formik.setFieldValue("timezone", option.name);
                            SetSelectedTimezone(option as any);
                          }}
                          options={timezones}
                          tittle={t("branch.views.timezone")}
                          error={
                            formik?.errors?.timezone &&
                            formik?.touched?.timezone
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("branch.views.title_app")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              checked={formik.values.communication}
                              onChange={() => {
                                if (formik.values.communication) {
                                  formik.setFieldValue("app_ios", "");
                                  formik.setFieldValue("app_android", "");
                                }
                              }}
                              id="communication"
                              name="communication"
                              type="checkbox"
                              aria-describedby="communication"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="appFitmewise"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.app_fitmewise")}
                            </label>
                          </div>
                        </div>
                      </div>
                      {formik.values.communication === false ? (
                        <>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("branch.views.ios_app")}
                              span={t("general.optional")}
                              id="app_ios"
                              name="app_ios"
                              type="url"
                              placeholder="http://app.com"
                              value={formik.values.app_ios}
                              onChange={formik.handleChange}
                              error={formik.errors.app_ios}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("branch.views.android_app")}
                              span={t("general.optional")}
                              id="app_android"
                              name="app_android"
                              type="url"
                              placeholder="http://privacy.com"
                              value={formik.values.app_android}
                              onChange={formik.handleChange}
                              error={formik.errors.app_android}
                            />
                          </div>
                        </>
                      ) : (
                        <></>
                      )}
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              checked={formik.values.is_chat}
                              onChange={() => {
                                formik.setFieldValue(
                                  "is_chat",
                                  !formik.values.is_chat
                                );
                              }}
                              id="is_chat"
                              name="is_chat"
                              type="checkbox"
                              aria-describedby="uses_chat"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="uses_chat"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.chat")}
                            </label>
                          </div>
                        </div>
                      </div>
                      {formik.values.is_chat ? (
                        <>
                          <div className="col-span-full">
                            <ToggleText
                              checked={checkedChat}
                              onChange={setcheckedChat}
                              texto={
                                checkedChat === false
                                  ? t("branch.views.branch")
                                  : "Fitmewise"
                              }
                            />
                          </div>
                          {checkedChat === false ? (
                            <div className="col-span-full">
                              <Select
                                selected={selectedSets}
                                setSelected={(option) => {
                                  setSelectedSets(option as any);
                                  formik.setFieldValue("sets", option.id);
                                }}
                                options={setsOptions}
                                tittle={"Sets"}
                              />
                            </div>
                          ) : null}
                        </>
                      ) : null}
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="uses_block_training"
                              name="uses_block_training"
                              type="checkbox"
                              aria-describedby="uses_block_training"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.block_training}
                              onChange={() => {
                                formik.setFieldValue(
                                  "block_training",
                                  !formik.values.block_training
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="uses_block_training"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.block_training")}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="uses_block_nutrition"
                              name="uses_block_nutrition"
                              type="checkbox"
                              aria-describedby="uses_block_nutrition"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.block_nutrition}
                              onChange={() => {
                                formik.setFieldValue(
                                  "block_nutrition",
                                  !formik.values.block_nutrition
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="uses_block_nutrition"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.block_nutrition")}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="uses_compliance_schedule"
                              name="uses_compliance_schedule"
                              type="checkbox"
                              aria-describedby="uses_compliance_schedule"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.compliance_schedule}
                              onChange={() => {
                                formik.setFieldValue(
                                  "compliance_schedule",
                                  !formik.values.compliance_schedule
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="uses_compliance_schedule"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.uses_compliance_schedule")}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("branch.views.title_dashboard")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              checked={
                                formik.values.customer_to_gym_subscription
                              }
                              onChange={() => {
                                formik.setFieldValue(
                                  "customer_to_gym_subscription",
                                  !formik.values.customer_to_gym_subscription
                                );
                              }}
                              id="customer_to_gym_subscription"
                              name="customer_to_gym_subscription"
                              type="checkbox"
                              aria-describedby="customer_to_gym_subscription"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="customer_to_gym_subscription"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.uses_fee")}
                            </label>
                          </div>
                        </div>
                      </div>
                      {formik.values.customer_to_gym_subscription ? (
                        <div className="col-span-full">
                          <div className="relative flex items-start">
                            <div className="flex h-6 items-center">
                              <input
                                id="uses_multi_center_quotas"
                                name="uses_multi_center_quotas"
                                type="checkbox"
                                aria-describedby="uses_multi_center_quotas"
                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                                checked={formik.values.multi_center_quotas}
                                onChange={() => {
                                  formik.setFieldValue(
                                    "multi_center_quotas",
                                    !formik.values.multi_center_quotas
                                  );
                                }}
                              />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                              <label
                                htmlFor="uses_multi_center_quotas"
                                className="font-medium text-gray-900"
                              >
                                {t("branch.views.multi-center_quotas")}
                              </label>
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="uses_lesson"
                              name="uses_lesson"
                              type="checkbox"
                              aria-describedby="uses_lesson"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.uses_lesson}
                              onChange={() => {
                                formik.setFieldValue(
                                  "uses_lesson",
                                  !formik.values.uses_lesson
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="uses_lesson"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check")}
                            </label>
                          </div>
                        </div>
                      </div>
                      {formik.values.uses_lesson ? (
                        <>
                          <div className="col-span-full">
                            <Select
                              addDefault={false}
                              selected={optionPenalizacion.find(
                                (option) =>
                                  option.id === formik.values.rule_penalty
                              )}
                              setSelected={(option) => {
                                formik.setFieldValue("rule_penalty", option.id);
                              }}
                              options={optionPenalizacion}
                              tittle={t("branch.views.rule_1")}
                              textDescribe={t("branch.views.rule_1_desc")}
                            />
                          </div>
                          <div className="col-span-full">
                            <SelectRuleMax
                              value={formik.values.rule_max}
                              onChange={(value) => {
                                formik.setFieldValue("rule_max", value);
                              }}
                              options={optionReservasUsuario}
                            />
                          </div>
                        </>
                      ) : null}
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="uses_assisteds"
                              name="uses_assisteds"
                              type="checkbox"
                              aria-describedby="uses_assisteds"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.uses_assisteds}
                              onChange={() => {
                                formik.setFieldValue(
                                  "uses_assisteds",
                                  !formik.values.uses_assisteds
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="uses_assisteds"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check_services")}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="slots"
                              name="slots"
                              type="checkbox"
                              aria-describedby="slots"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.slots}
                              onChange={() => {
                                formik.setFieldValue(
                                  "slots",
                                  !formik.values.slots
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="slots"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check_reservas")}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="catchment_module"
                              name="catchment_module"
                              type="checkbox"
                              aria-describedby="catchment_module"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.catchment_module}
                              onChange={() => {
                                formik.setFieldValue(
                                  "catchment_module",
                                  !formik.values.catchment_module
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="catchment_module"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check_captation")}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="recovery_module"
                              name="recovery_module"
                              type="checkbox"
                              aria-describedby="recovery_module"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.recovery_module}
                              onChange={() => {
                                formik.setFieldValue(
                                  "recovery_module",
                                  !formik.values.recovery_module
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="recovery_module"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check_recuperacion")}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="ticket_module"
                              name="ticket_module"
                              type="checkbox"
                              aria-describedby="ticket_module"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.ticket_module}
                              onChange={() => {
                                formik.setFieldValue(
                                  "ticket_module",
                                  !formik.values.ticket_module
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="ticket_module"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check_assistant")}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="store"
                              name="store"
                              type="checkbox"
                              aria-describedby="store"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.store}
                              onChange={() => {
                                formik.setFieldValue(
                                  "store",
                                  !formik.values.store
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="store"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check_shop")}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="course_module"
                              name="course_module"
                              type="checkbox"
                              aria-describedby="course_module"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.course_module}
                              onChange={() => {
                                formik.setFieldValue(
                                  "course_module",
                                  !formik.values.course_module
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="course_module"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check_cursos")}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="access_module"
                              name="access_module"
                              type="checkbox"
                              aria-describedby="access_module"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.access_module}
                              onChange={() => {
                                formik.setFieldValue(
                                  "access_module",
                                  !formik.values.access_module
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="access_module"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check_access")}
                            </label>
                          </div>
                        </div>
                      </div>

                      {formik.values.access_module ? (
                        <div className="col-span-full">
                          <Select
                            selected={optionControlAccess.find(
                              (option) =>
                                option.id === formik.values.access_module_type
                            )}
                            setSelected={(option) => {
                              formik.setFieldValue(
                                "access_module_type",
                                option.id
                              );
                            }}
                            options={optionControlAccess}
                            tittle={t("branch.views.access")}
                          />
                        </div>
                      ) : null}
                      <div className="col-span-full">
                        <Select
                          selected={optionCreacionUsers.find(
                            (option) => option.id === formik.values.type_branch
                          )}
                          setSelected={(option) => {
                            formik.setFieldValue("type_branch", option.id);
                          }}
                          options={optionCreacionUsers}
                          tittle={t("branch.views.type")}
                        />
                      </div>
                      {formik.values.type_branch === 1 ? (
                        <div className="col-span-full">
                          <InputFilepdf
                            name="contract"
                            id="contract"
                            label={t("share.views.Subir_Reemplazar_contrato")}
                            span=""
                            spanButton={t("share.views.up_contracto")}
                            description={t("general.format_pdf")}
                            selectedFileName={
                              formik?.values?.contract
                                ? formik?.values?.contract
                                : null
                            }
                            error={
                              formik?.errors?.contract &&
                              (formik?.touched?.contract as boolean)
                            }
                            onFileChange={(file) => {
                              const contractURL = URL.createObjectURL(file);
                              formik.setFieldValue("contract", contractURL);
                            }}
                            onRemoveFile={() => {
                              formik.setFieldValue("contract", null);
                            }}
                            tittleDelete={t("general.delete_archive")}
                          />
                        </div>
                      ) : null}
                      <div className="col-span-full">
                        <MultiSelect
                          options={optionsMarketing}
                          selectedOptions={selectedMarketing}
                          setSelectedOptions={(options) => {
                            setSelectedOptionsMarketing(options as any[]);
                            const selectedIds = Array.isArray(options)
                              ? options.map((option) => option.id)
                              : [];
                            formik.setFieldValue(
                              "available_marketing",
                              selectedIds
                            );
                          }}
                          title={t("branch.views.marketing")}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("branch.views.title_method_payment")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              checked={formik.values.accounting_module}
                              onChange={() => {
                                formik.setFieldValue(
                                  "accounting_module",
                                  !formik.values.accounting_module
                                );
                              }}
                              id="accounting_module"
                              name="accounting_module"
                              type="checkbox"
                              aria-describedby="accounting_module"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="accounting_module"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check_contabilidad")}
                            </label>
                          </div>
                        </div>
                      </div>
                      {formik.values.accounting_module ? (
                        <div className="col-span-full">
                          <Select
                            span={t("general.mandatory")}
                            selected={optionContabilidad.find(
                              (option) =>
                                option.id === formik.values.tax_authorities
                            )}
                            setSelected={(option) => {
                              formik.setFieldValue(
                                "tax_authorities",
                                option.name
                              );
                            }}
                            options={optionContabilidad}
                            tittle={t("branch.views.tax_authorities")}
                          />
                        </div>
                      ) : null}
                      {formik.values.tax_authorities === 2 ? (
                        <>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("branch.views.code_activation")}
                              span={t("general.mandatory")}
                              id="gti_code"
                              name="gti_code"
                              type="text"
                              onChange={formik.handleChange}
                              value={formik.values.gti_code}
                              error={
                                formik?.errors?.gti_code &&
                                (formik?.touched?.gti_code as boolean)
                              }
                              textError={formik?.errors?.gti_code as string}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("branch.views.user")}
                              span={t("general.mandatory")}
                              id="gti_user"
                              name="gti_user"
                              type="text"
                              placeholder={t("branch.views.user")}
                              onChange={formik.handleChange}
                              value={formik.values.gti_user}
                              error={
                                formik?.errors?.gti_user &&
                                (formik?.touched?.gti_user as boolean)
                              }
                              textError={formik?.errors?.gti_user as string}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("branch.views.pass")}
                              span={t("general.mandatory")}
                              id="gti_password"
                              name="gti_password"
                              type="text"
                              placeholder={t("branch.views.pass")}
                              onChange={formik.handleChange}
                              value={formik.values.gti_password}
                              error={
                                formik?.errors?.gti_password &&
                                (formik?.touched?.gti_password as boolean)
                              }
                              textError={formik?.errors?.gti_password as string}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("branch.views.terminal")}
                              span={t("general.mandatory")}
                              id="gti_terminal"
                              name="gti_terminal"
                              type="text"
                              placeholder={t("branch.views.terminal")}
                              onChange={formik.handleChange}
                              value={formik.values.gti_terminal}
                              error={
                                formik?.errors?.gti_terminal &&
                                (formik?.touched?.gti_terminal as boolean)
                              }
                              textError={formik?.errors?.gti_terminal as string}
                            />
                          </div>
                        </>
                      ) : null}
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="payment_card"
                              name="payment_card"
                              type="checkbox"
                              aria-describedby="payment_card"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.payment_card}
                              onChange={() => {
                                formik.setFieldValue(
                                  "payment_card",
                                  !formik.values.payment_card
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="payment_card"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.check_paymentOnline")}
                            </label>
                          </div>
                        </div>
                      </div>
                      {formik.values.payment_card ? (
                        <>
                          <div className="col-span-full">
                            <Select
                              addDefault={false}
                              selected={optionPagos.find(
                                (option) =>
                                  option.name === formik.values.gateway
                              )}
                              setSelected={(option) => {
                                formik.setFieldValue("gateway", option.name);
                              }}
                              options={optionPagos}
                              tittle={t("branch.views.payment_gateway")}
                            />
                          </div>

                          {formik.values.gateway === "Stripe" ||
                          formik.values.gateway === "Wompi" ? (
                            <>
                              <div className="col-span-full">
                                <InputWithCornerHint
                                  label={t("branch.views.public_key_payment")}
                                  span={t("general.mandatory")}
                                  id="public_key_payment"
                                  name="public_key_payment"
                                  type="text"
                                  placeholder={t(
                                    "branch.views.public_key_payment"
                                  )}
                                  onChange={formik.handleChange}
                                  value={formik.values.public_key_payment}
                                  error={
                                    formik?.errors?.public_key_payment &&
                                    (formik?.touched
                                      ?.public_key_payment as boolean)
                                  }
                                  textError={
                                    formik?.errors?.public_key_payment as string
                                  }
                                />
                              </div>
                              <div className="col-span-full">
                                <InputWithCornerHint
                                  label={t("branch.views.private_key_payment")}
                                  span={t("general.mandatory")}
                                  id="private_key_payment"
                                  name="private_key_payment"
                                  type="text"
                                  placeholder={t(
                                    "branch.views.private_key_payment"
                                  )}
                                  onChange={formik.handleChange}
                                  value={formik.values.private_key_payment}
                                  error={
                                    formik?.errors?.private_key_payment &&
                                    (formik?.touched
                                      ?.private_key_payment as boolean)
                                  }
                                  textError={
                                    formik?.errors
                                      ?.private_key_payment as string
                                  }
                                />
                              </div>
                              {formik.values.gateway === "Wompi" ? (
                                <>
                                  <div className="col-span-full">
                                    <InputWithCornerHint
                                      label={t("branch.views.prod_integrity")}
                                      span={t("general.mandatory")}
                                      id="prod_integrity"
                                      name="prod_integrity"
                                      type="text"
                                      placeholder={t(
                                        "branch.views.private_key_payment"
                                      )}
                                      onChange={formik.handleChange}
                                      value={formik.values.prod_integrity}
                                      error={
                                        formik?.errors?.prod_integrity &&
                                        (formik?.touched
                                          ?.prod_integrity as boolean)
                                      }
                                      textError={
                                        formik?.errors?.prod_integrity as string
                                      }
                                    />
                                  </div>
                                </>
                              ) : (
                                <></>
                              )}
                            </>
                          ) : (
                            <></>
                          )}
                        </>
                      ) : null}
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="direct_debit_module"
                              name="direct_debit_module"
                              type="checkbox"
                              aria-describedby="direct_debit_module"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              checked={formik.values.direct_debit_module}
                              onChange={() => {
                                formik.setFieldValue(
                                  "direct_debit_module",
                                  !formik.values.direct_debit_module
                                );
                              }}
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="direct_debit_module"
                              className="font-medium text-gray-900"
                            >
                              {t("branch.views.uses_direct_debit")}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("branch.views.uses_fmw_subscription")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              checked={formik.values.uses_subscription}
                              onChange={() => {
                                formik.setFieldValue(
                                  "uses_subscription",
                                  !formik.values.uses_subscription
                                );
                              }}
                              id="uses_subscription"
                              name="uses_subscription"
                              type="checkbox"
                              aria-describedby="uses_subscription"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label
                              htmlFor="uses_subscription"
                              className="font-medium text-gray-900"
                            >
                              {t(
                                "branch.views.uses_fmw_subscription_description"
                              )}
                            </label>
                          </div>
                        </div>
                      </div>
                      {formik.values.uses_subscription && (
                        <>
                          <div className="col-span-full">
                            <MultiSelect
                              span={t("general.optional")}
                              options={productsQuery.data}
                              selectedOptions={
                                formik.values.available_subscriptions
                              }
                              setSelectedOptions={(selected) => {
                                formik.setFieldValue(
                                  "available_subscriptions",
                                  Array.isArray(selected)
                                    ? selected.map((subscription) => ({
                                        id: subscription.id,
                                        name: subscription.name,
                                      }))
                                    : {
                                        id: selected.id,
                                        name: selected.name,
                                      }
                                );
                              }}
                              title={t("branch.views.subscriptions")}
                            />
                          </div>
                          <div className="col-span-full">
                            <Select
                              addDefault={false}
                              span={t("general.optional")}
                              selected={contractsQuery.data.find(
                                (option) =>
                                  option.id == formik.values.contract_id
                              )}
                              setSelected={(option) => {
                                formik.setFieldValue("contract_id", option.id);
                              }}
                              options={contractsQuery.data}
                              tittle={t("branch.views.contracts_available")}
                              error={
                                formik.errors.contract_id &&
                                (formik.touched.contract_id as boolean)
                              }
                              textError={formik.errors.contract_id as string}
                            />
                          </div>
                          <div className="col-span-full">
                            <div className="relative flex items-start">
                              <div className="flex h-6 items-center">
                                <input
                                  checked={formik.values.subscription_blocking}
                                  onChange={() => {
                                    formik.setFieldValue(
                                      "subscription_blocking",
                                      !formik.values.subscription_blocking
                                    );
                                  }}
                                  id="subscription_blocking"
                                  name="subscription_blocking"
                                  type="checkbox"
                                  aria-describedby="subscription_blocking"
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                                />
                              </div>
                              <div className="ml-3 text-sm leading-6">
                                <label
                                  htmlFor="subscription_blocking"
                                  className="font-medium text-gray-900"
                                >
                                  {t(
                                    "branch.views.subscription_blocking_description"
                                  )}
                                </label>
                              </div>
                            </div>
                          </div>
                          {formik.values.subscription_blocking && (
                            <div className="col-span-full">
                              <InputWithCornerHint
                                label={t("branch.views.trial_period_end_date")}
                                span={t("general.optional")}
                                id="trial_period_days"
                                name="trial_period_days"
                                type="number"
                                placeholder={t(
                                  "branch.views.trial_period_end_date"
                                )}
                                value={formik.values.trial_period_days}
                                onChange={formik.handleChange}
                                error={
                                  formik.errors.trial_period_days &&
                                  (formik.touched.trial_period_days as boolean)
                                }
                                textError={
                                  formik.errors.trial_period_days as string
                                }
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </DialogCreate>
          );
        }}
      </Formik> */}
    </Fragment>
  );
};
